import { useState } from "react";
import { useParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import client, { API } from "@/lib/api";
import { Loader2, LinkIcon, MapPin, Images } from "lucide-react";
import { BeforeAfterSlider } from "@/components/BeforeAfterSlider";
import { Lightbox } from "@/components/Lightbox";

const LOGO = "/tilesmate-logo.png";
const full = (id) => `${API}/public/portfolio/media/${id}`;

// Image with copy/save deterrents (blocks right-click, drag & long-press save).
function ZoomImg({ src, alt, className, onClick }) {
  return (
    <img
      src={src}
      alt={alt}
      draggable={false}
      onDragStart={(e) => e.preventDefault()}
      onContextMenu={(e) => e.preventDefault()}
      onClick={onClick}
      loading="lazy"
      className={className}
      style={{ WebkitUserSelect: "none", userSelect: "none", WebkitTouchCallout: "none" }}
    />
  );
}

function ProjectShowcase({ p, onZoom }) {
  return (
    <section className="mb-10" data-testid="showcase-project">
      <h2 className="font-display text-xl font-extrabold text-ink">{p.title}</h2>
      {p.location && <p className="mt-0.5 flex items-center gap-1 text-sm text-muted-foreground"><MapPin className="h-3.5 w-3.5" />{p.location}</p>}
      {p.description && <p className="mt-2 text-sm leading-relaxed text-slate-600">{p.description}</p>}

      {p.before_after?.length > 0 && (
        <div className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-2">
          {p.before_after.map((ba, i) => (
            <div key={i}>
              <BeforeAfterSlider beforeUrl={full(ba.before_media_id)} afterUrl={full(ba.after_media_id)} />
              {ba.caption && <p className="mt-1.5 text-center text-xs text-muted-foreground">{ba.caption}</p>}
            </div>
          ))}
        </div>
      )}

      {p.images?.length > 0 && (
        <div className="mt-4 grid grid-cols-2 gap-3 sm:grid-cols-3">
          {p.images.map((im, i) => (
            <figure key={i} className="overflow-hidden rounded-xl border border-slate-200 bg-slate-50">
              <ZoomImg src={full(im.media_id)} alt={im.caption || p.title} onClick={() => onZoom?.((p.images || []).map((x) => ({ url: full(x.media_id), caption: x.caption })), i)} className="aspect-square w-full cursor-zoom-in object-cover transition-transform hover:scale-[1.02]" />
              {im.caption && <figcaption className="px-2 py-1 text-[11px] text-muted-foreground">{im.caption}</figcaption>}
            </figure>
          ))}
        </div>
      )}

      {(!p.images?.length && !p.before_after?.length) && (
        <div className="mt-4 grid place-items-center rounded-xl border border-dashed border-slate-200 py-12 text-slate-300"><Images className="h-7 w-7" /></div>
      )}
    </section>
  );
}

export default function PortfolioShare({ kind }) {
  const { token } = useParams();
  const { data, isLoading, isError } = useQuery({
    queryKey: ["public-portfolio", kind, token],
    queryFn: async () => (await client.get(`/public/portfolio/${kind}/${token}`)).data,
    retry: false,
  });
  const [lb, setLb] = useState(null);

  if (isLoading) return <div className="flex min-h-screen items-center justify-center bg-[#F8FAFC]"><Loader2 className="h-7 w-7 animate-spin text-primary" /></div>;

  if (isError || !data) {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center gap-3 bg-[#F8FAFC] px-6 text-center" data-testid="showcase-unavailable">
        <div className="grid h-12 w-12 place-items-center rounded-2xl bg-primary/10 text-primary"><LinkIcon className="h-6 w-6" /></div>
        <h1 className="font-display text-xl font-bold text-ink">This link isn't available</h1>
        <p className="max-w-sm text-sm text-muted-foreground">The link may have been turned off. Please contact your TilesMate consultant for an up-to-date link.</p>
      </div>
    );
  }

  const title = kind === "category" ? data.name : data.title;
  const subtitle = kind === "category" ? (data.description || "Our work") : (data.category_name || "Our work");
  const projects = kind === "category" ? (data.projects || []) : [data];

  return (
    <div className="min-h-screen select-none bg-[#F8FAFC]" data-testid="showcase-page" onContextMenu={(e) => e.preventDefault()}>
      <header className="border-b-[3px] border-[#F5A623] bg-white">
        <div className="mx-auto flex max-w-3xl items-center justify-between gap-4 px-6 py-5">
          <img src={LOGO} alt="TilesMate" className="h-10 w-auto" />
          <div className="text-right">
            <div className="font-display text-sm font-bold text-ink">Our Work</div>
            <div className="text-xs text-muted-foreground">{subtitle}</div>
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-3xl px-6 py-8">
        {kind === "category" && data.cover_media_id && (
          <div className="mb-6 overflow-hidden rounded-2xl border border-slate-100 bg-slate-50">
            <ZoomImg src={full(data.cover_media_id)} alt={title} onClick={() => setLb({ images: [{ url: full(data.cover_media_id), caption: title }], index: 0 })} className="aspect-[16/9] w-full cursor-zoom-in object-contain" />
          </div>
        )}
        <h1 className="mb-1 font-display text-2xl font-extrabold text-ink" data-testid="showcase-title">{title}</h1>
        {kind === "category" && data.description && <p className="mb-6 text-sm text-muted-foreground">{data.description}</p>}
        <div className="mt-6">
          {projects.length === 0
            ? <p className="rounded-2xl border border-dashed border-slate-200 bg-white py-12 text-center text-sm text-muted-foreground">Nothing to show here yet.</p>
            : projects.map((p, i) => <ProjectShowcase key={i} p={p} onZoom={(images, index) => setLb({ images, index })} />)}
        </div>

        <footer className="mt-6 border-t border-slate-200 pt-5 text-center text-xs text-muted-foreground">
          <p>Showcase prepared by TilesMate. Finishes shown are examples of our completed work.</p>
        </footer>
      </main>

      {lb && <Lightbox images={lb.images} startIndex={lb.index} onClose={() => setLb(null)} />}
    </div>
  );
}
